import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ImageHoverPreview,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
} from "@elit-hair/elit-ui";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { AVAILABLE_BRANDS, AVAILABLE_CATEGORIES } from "@/constants/treatments";
import type { CreateTreatmentRequest, TreatmentFormData, TreatmentModalProps } from "@/types/treatment.types";
import { treatmentValidationSchema } from "@/utils/treatment-validation";

export const TreatmentModal = ({ isOpen, onClose, treatment, onSubmit }: TreatmentModalProps) => {
    const { t } = useTranslation();
    const isEdit = !!treatment;
    const [formData, setFormData] = useState<TreatmentFormData>({
        title: "",
        brand: "",
        description: "",
        image: "",
        price: 0,
        discounts: {
            actual: {
                price: 0,
            },
        },
        duration: 30,
        category: "",
        isAppHighlighted: false,
    });
    const [errors, setErrors] = useState<Partial<Record<keyof TreatmentFormData | string, string>>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset form when modal opens/closes or treatment changes
    useEffect(() => {
        if (isOpen) {
            if (treatment) {
                setFormData({
                    title: treatment.title,
                    brand: treatment.brand,
                    description: treatment.description || "",
                    image: treatment.image || "",
                    price: treatment.price,
                    discounts: treatment.discounts,
                    duration: treatment.duration,
                    category: treatment.category,
                    isAppHighlighted: treatment.isAppHighlighted,
                });
            } else {
                setFormData({
                    title: "",
                    brand: "",
                    description: "",
                    image: "",
                    price: 0,
                    discounts: {
                        actual: {
                            price: 0,
                        },
                    },
                    duration: 30,
                    category: "",
                    isAppHighlighted: false,
                });
            }
            setErrors({});
        }
    }, [isOpen, treatment]);

    const validate = (): boolean => {
        try {
            treatmentValidationSchema.parse(formData);
            setErrors({});
            return true;
        } catch (error) {
            if (error instanceof z.ZodError) {
                const newErrors: Partial<Record<keyof TreatmentFormData | string, string>> = {};
                error.errors.forEach((err) => {
                    const path = err.path[0] as keyof TreatmentFormData;
                    if (path === "discounts" && err.path.length > 1) {
                        // Handle nested discounts errors
                        const discountField = err.path[1] as string;
                        if (discountField === "actual" && err.path.length > 2) {
                            const actualField = err.path[2] as string;
                            newErrors[`discounts.actual.${actualField}`] = err.message;
                        } else {
                            newErrors[`discounts.${discountField}`] = err.message;
                        }
                    } else {
                        newErrors[path] = err.message;
                    }
                });
                setErrors(newErrors);
            }
            return false;
        }
    };

    const handleInputChange = (field: keyof TreatmentFormData, value: string | number | boolean) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validateDiscountPrice = (discountPrice: number, regularPrice: number) => {
        const errorKey = "discounts.actual.price";
        const hasError = discountPrice > 0 && discountPrice >= regularPrice;

        setErrors((prev) => {
            const filtered = Object.fromEntries(Object.entries(prev).filter(([key]) => key !== errorKey));
            return {
                ...filtered,
                ...(hasError && { [errorKey]: "discount_price_must_be_lower_than_normal_price" }),
            };
        });
    };

    const handleDiscountChange = (value: string) => {
        let numValue: number;
        if (value === "") {
            numValue = 0;
        } else {
            const parsed = parseFloat(value);
            numValue = isNaN(parsed) ? 0 : parsed;
        }

        validateDiscountPrice(numValue, formData.price);

        setFormData((prev) => ({
            ...prev,
            discounts: {
                actual: {
                    price: numValue,
                },
            },
        }));
    };

    const handleSubmit = async () => {
        if (!validate()) {
            return;
        }

        setIsSubmitting(true);
        try {
            const submitData: CreateTreatmentRequest = {
                title: formData.title.trim(),
                brand: formData.brand,
                description: formData.description?.trim() || "",
                image: formData.image?.trim() || "",
                price: formData.price,
                discounts:
                    formData.discounts?.actual.price !== undefined && formData.discounts.actual.price > 0
                        ? formData.discounts
                        : undefined,
                duration: formData.duration,
                category: formData.category,
                isAppHighlighted: formData.isAppHighlighted,
            };
            await onSubmit(submitData);
            onClose();
        } catch (error) {
            // Capture error in Sentry for monitoring
            Sentry.captureException(error, {
                tags: {
                    component: "TreatmentModal",
                    action: "submit",
                },
            });

            // Log the actual error response from the backend
            if (
                error &&
                typeof error === "object" &&
                "response" in error &&
                error.response &&
                typeof error.response === "object" &&
                "data" in error.response
            ) {
                const axiosError = error as {
                    response?: {
                        status?: number;
                        data?: {
                            message?: string;
                            error?: string;
                            errors?: Array<unknown>;
                        };
                    };
                };
                const errorData = axiosError.response?.data;

                // Build detailed error message
                let errorMessage = errorData?.message || "Failed to submit treatment";

                // Handle validation errors
                if (errorData?.errors && Array.isArray(errorData.errors)) {
                    const validationErrors = errorData.errors
                        .map((err: unknown) => {
                            if (typeof err === "string") return err;
                            if (err && typeof err === "object" && "message" in err) {
                                return String(err.message);
                            }
                            return String(err);
                        })
                        .join(", ");
                    errorMessage = validationErrors || errorMessage;
                }

                // Show error to user
                toast.error(errorMessage);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{isEdit ? t("edit_treatment") : t("add_new_treatment")}</DialogTitle>
                    <DialogDescription>
                        {isEdit ? t("update_treatment_details") : t("add_new_treatment_description")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="title">{t("title")} *</Label>
                        <Input
                            id="title"
                            placeholder={t("enter_treatment_title")}
                            value={formData.title}
                            onChange={(e) => handleInputChange("title", e.target.value)}
                        />
                        {errors.title && <p className="text-sm text-red-500">{t(errors.title)}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="brand">{t("brand")} *</Label>
                            <Select value={formData.brand} onValueChange={(value) => handleInputChange("brand", value)}>
                                <SelectTrigger id="brand">
                                    <SelectValue placeholder={t("select_brand")} />
                                </SelectTrigger>
                                <SelectContent>
                                    {AVAILABLE_BRANDS.map((brand) => (
                                        <SelectItem key={brand} value={brand}>
                                            {brand}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {errors.brand && <p className="text-sm text-red-500">{t(errors.brand)}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="category">{t("category")} *</Label>
                            <Select
                                value={formData.category}
                                onValueChange={(value) => handleInputChange("category", value)}
                            >
                                <SelectTrigger id="category">
                                    <SelectValue placeholder={t("select_category")} />
                                </SelectTrigger>
                                <SelectContent>
                                    {AVAILABLE_CATEGORIES.map((category) => (
                                        <SelectItem key={category} value={category}>
                                            {category}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {errors.category && <p className="text-sm text-red-500">{t(errors.category)}</p>}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="price">{t("price")} *</Label>
                            <Input
                                id="price"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                value={formData.price || ""}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "") {
                                        handleInputChange("price", 0);
                                    } else {
                                        const numValue = parseFloat(value);
                                        if (!isNaN(numValue)) {
                                            handleInputChange("price", numValue);
                                            // Re-validate discount price if it exists
                                            if (formData.discounts?.actual.price !== undefined) {
                                                validateDiscountPrice(formData.discounts.actual.price, numValue);
                                            }
                                        }
                                    }
                                }}
                            />
                            {errors.price && <p className="text-sm text-red-500">{t(errors.price)}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="discountPrice">{t("discount_price")}</Label>
                            <Input
                                id="discountPrice"
                                type="number"
                                min="0"
                                max={formData.price > 0 ? formData.price - 0.01 : undefined}
                                step="0.01"
                                placeholder="0.00"
                                value={formData.discounts?.actual.price || ""}
                                onChange={(e) => handleDiscountChange(e.target.value)}
                            />
                            {(errors.discounts || errors["discounts.actual.price"]) && (
                                <p className="text-sm text-red-500">
                                    {t(
                                        errors["discounts.actual.price"] ||
                                            errors.discounts ||
                                            "discount_price_must_be_lower_than_normal_price"
                                    )}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="duration">{t("duration")} *</Label>
                        <Input
                            id="duration"
                            type="number"
                            min="1"
                            step="1"
                            placeholder="30"
                            value={formData.duration || ""}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === "") {
                                    handleInputChange("duration", 0);
                                } else {
                                    const numValue = parseInt(value);
                                    if (!isNaN(numValue)) {
                                        handleInputChange("duration", numValue);
                                    }
                                }
                            }}
                        />
                        {errors.duration && <p className="text-sm text-red-500">{t(errors.duration)}</p>}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="image">{t("image_url")} *</Label>
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <Input
                                    id="image"
                                    type="url"
                                    placeholder="https://..."
                                    value={formData.image || ""}
                                    onChange={(e) => handleInputChange("image", e.target.value)}
                                />
                                {errors.image && <p className="text-sm text-red-500">{t(errors.image)}</p>}
                            </div>
                            {formData.image && formData.image.trim() && (
                                <div className="shrink-0">
                                    <ImageHoverPreview
                                        src={formData.image}
                                        alt={t("image_preview")}
                                        thumbnailSize="lg"
                                        previewSize={320}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">{t("description")} *</Label>
                        <textarea
                            id="description"
                            className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder={t("enter_description")}
                            value={formData.description || ""}
                            onChange={(e) => handleInputChange("description", e.target.value)}
                            rows={4}
                        />
                        {errors.description && <p className="text-sm text-red-500">{t(errors.description)}</p>}
                    </div>

                    <div className="flex items-center space-x-2">
                        <Switch
                            id="isAppHighlighted"
                            checked={formData.isAppHighlighted}
                            onCheckedChange={(checked) => handleInputChange("isAppHighlighted", checked)}
                        />
                        <Label htmlFor="isAppHighlighted" className="text-sm font-normal cursor-pointer">
                            {t("app_highlighted")}
                        </Label>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                        {t("cancel")}
                    </Button>
                    <Button onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? t("saving") : t("save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
